import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, MapPin, Package, DollarSign, LogOut, TrendingUp } from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const CityHallDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [summary, setSummary] = useState(null);
  const [byBeach, setByBeach] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/reports/city-hall/summary').then((r) => setSummary(r.data)),
      api.get('/reports/city-hall/consumption').then((r) => setByBeach(r.data)),
    ])
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner size="lg" />;

  const exportCSV = () => {
    const csv = [
      ['Praia', 'Total Pedidos', 'Faturamento (R$)'],
      ...byBeach.map((b) => [b.beach_name, b.total_orders, b.total_revenue.toFixed(2)]),
    ]
      .map((row) => row.join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `beach-pay-prefeitura-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  return (
    <div className="min-h-screen bg-muted/30" data-testid="cityhall-dashboard">
      <div className="bg-gradient-to-r from-secondary to-blue-600 text-white sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-['Outfit'] text-xl font-bold flex items-center gap-2">
              <Building2 className="w-6 h-6" />
              Prefeitura · Beach Pay
            </h1>
            <p className="text-xs text-white/80">Painel de relatórios consolidados</p>
          </div>
          <button onClick={() => { logout(); navigate('/login'); }} className="p-2 hover:bg-white/10 rounded-full">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl p-4 border border-border">
            <Package className="w-6 h-6 text-secondary mb-2" />
            <p className="text-xs text-muted-foreground">Pedidos totais</p>
            <p className="font-bold text-2xl">{summary?.total_orders || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-border">
            <TrendingUp className="w-6 h-6 text-primary mb-2" />
            <p className="text-xs text-muted-foreground">Últimos 30 dias</p>
            <p className="font-bold text-2xl">{summary?.recent_orders_30d || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-border">
            <DollarSign className="w-6 h-6 text-accent mb-2" />
            <p className="text-xs text-muted-foreground">Faturamento</p>
            <p className="font-bold text-2xl">R$ {summary?.total_revenue?.toFixed(0) || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-border">
            <MapPin className="w-6 h-6 text-secondary mb-2" />
            <p className="text-xs text-muted-foreground">Praias ativas</p>
            <p className="font-bold text-2xl">{summary?.total_beaches || 0}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="font-['Outfit'] font-bold text-lg">Consumo por Praia</h2>
            <button
              data-testid="export-csv"
              onClick={exportCSV}
              className="px-3 py-1.5 bg-primary text-primary-foreground rounded-full text-sm font-semibold"
            >
              Exportar CSV
            </button>
          </div>
          <div className="divide-y divide-border">
            {byBeach.length === 0 ? (
              <p className="p-6 text-center text-muted-foreground">Sem dados ainda</p>
            ) : (
              byBeach.map((b, idx) => (
                <div key={idx} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🏖️</span>
                    <div>
                      <p className="font-semibold">{b.beach_name}</p>
                      <p className="text-xs text-muted-foreground">{b.total_orders} pedidos</p>
                    </div>
                  </div>
                  <p className="font-bold text-primary">R$ {b.total_revenue.toFixed(2)}</p>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-secondary/5 border border-secondary/20 rounded-xl p-4 text-sm">
          <p className="font-semibold mb-1">💡 Indicadores turísticos</p>
          <p className="text-muted-foreground text-xs">
            Acompanhe o movimento das praias em tempo real. Dados consolidados para tomada de decisão sobre infraestrutura, segurança e turismo.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CityHallDashboard;
